// Adaptive Layout System
// Computes layout configuration dynamically from physical display size,
// orientation, and habit count. Produces config shapes matching the existing
// component interfaces (layoutConfigs.js format).

/**
 * Estimate physical screen size in inches from JS-available metrics.
 * On Android, CSS px ≈ dp, and 160dp ≈ 1 physical inch.
 * Accuracy: ~10-15% (Pixel 6: 6.4" actual → ~6.26" estimated).
 */
export function estimatePhysicalSize() {
  var w = window.screen.width / 160;
  var h = window.screen.height / 160;
  var diagonal = Math.sqrt(w * w + h * h);

  return {
    widthInches: w,
    heightInches: h,
    diagonalInches: diagonal
  };
}

// --- Sizing lookup helpers ---

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function roundToEven(value) {
  return Math.round(value / 2) * 2;
}

/**
 * Select dot size class based on physical diagonal and yearDots tier.
 */
function computeDotSize(diagonalInches, tier) {
  if (tier === 'ultra-compact') return 'w-1 h-1';
  if (tier === 'compact') {
    if (diagonalInches < 6) return 'w-1 h-1';
    if (diagonalInches < 8) return 'w-1.5 h-1.5';
    return 'w-2 h-2';
  }
  // standard
  if (diagonalInches < 5.5) return 'w-1.5 h-1.5';
  if (diagonalInches < 7) return 'w-2 h-2';
  if (diagonalInches < 10) return 'w-3 h-3';
  return 'w-4 h-4';
}

/**
 * Select button size (Tailwind class) based on physical diagonal and habit count.
 * Uses a tier-based approach: diagonal sets the base comfortable size,
 * habit count scales it down. Aligned with existing static layout configs:
 *   mobile portrait: h-10, mobile landscape: h-8, tablet portrait: h-20,
 *   tablet landscape: h-14, desktop: h-16/h-20
 */
function computeButtonSize(physicalWidthInches, diagonalInches, habitCount, orientation) {
  // Base button size from physical diagonal (Tailwind units, e.g. 10 = h-10).
  var base;
  if (diagonalInches < 6) {
    base = 10;
  } else if (diagonalInches < 7) {
    base = 12;
  } else if (diagonalInches < 9) {
    base = 16;
  } else if (diagonalInches < 11) {
    base = 18;
  } else {
    base = 20;
  }

  // Landscape uses smaller buttons (less vertical space)
  if (orientation === 'landscape') {
    base = Math.max(8, base - 4);
  }

  // Scale down based on habit count
  var scaled;
  if (habitCount <= 3) {
    scaled = base;
  } else if (habitCount <= 6) {
    scaled = base - 2;
  } else if (habitCount <= 10) {
    scaled = base - 4;
  } else {
    scaled = base - 6;
  }

  var buttonTw = roundToEven(clamp(scaled, 8, 20));
  return 'h-' + buttonTw + ' w-' + buttonTw;
}

/**
 * Select text size class based on physical diagonal.
 */
function computeTextSize(diagonalInches) {
  if (diagonalInches < 6) return 'text-xs';
  if (diagonalInches < 7) return 'text-sm';
  if (diagonalInches < 10) return 'text-base';
  return 'text-lg';
}

function computeDayTextSize(diagonalInches) {
  if (diagonalInches < 7) return 'text-[0.625rem]';
  if (diagonalInches < 10) return 'text-xs';
  return 'text-sm';
}

function computeHeaderTextSize(diagonalInches, tier) {
  if (tier === 'ultra-compact') return 'text-xs';
  if (tier === 'compact') return 'text-sm';
  if (diagonalInches < 7) return 'text-sm';
  if (diagonalInches < 10) return 'text-xl';
  return 'text-2xl';
}

function computePadding(diagonalInches, tier) {
  if (tier === 'ultra-compact') return 'p-1';
  if (tier === 'compact') return 'p-2';
  if (diagonalInches < 6) return 'p-2';
  if (diagonalInches < 7) return 'p-2';
  if (diagonalInches < 10) return 'p-4';
  return 'p-6';
}

// Row gap inside the weekView grid. Hardcoded `space-y-4` (16 px) was eating
// the per-row budget on phone-landscape — adaptive rowHeight is 0.25" (~40
// CSS px) but a 32 px button + 16 px gap leaves no room for the rest of the
// chrome. Tier this off orientation, diagonal, and habit count so the gap
// shrinks alongside the rest of the budget.
function computeRowGap(diagonalInches, orientation, habitCount) {
  if (orientation !== 'landscape') return 'space-y-4';
  if (diagonalInches < 7) {
    return habitCount > 5 ? 'space-y-0.5' : 'space-y-1';
  }
  if (diagonalInches < 10) return 'space-y-2';
  return 'space-y-4';
}

function computeNameColWidth(diagonalInches, orientation) {
  // Phone portrait has only ~411px of CSS width after padding, so 6rem is
  // about all we can afford there. Landscape on the same phone has 914px
  // across, which is plenty — give the habit name enough room to show
  // "Exercise" or "Play VR" without truncation.
  if (diagonalInches < 7) return orientation === 'landscape' ? '8rem' : '6rem';
  if (diagonalInches < 10) return '8rem';
  return '11.25rem';
}

function computeStarSize(diagonalInches) {
  if (diagonalInches < 7) return 'w-4 h-4';
  return 'w-5 h-5';
}

function computePlusSize(diagonalInches) {
  if (diagonalInches < 7) return 'w-3 h-3';
  return 'w-5 h-5';
}

/**
 * Determine the yearDots tier based on remaining space after weekView.
 * Returns 'standard', 'compact', or 'ultra-compact'.
 */
function determineYearDotsTier(remainingInches, diagonalInches) {
  // How much space each tier needs
  var standardNeeded = diagonalInches < 7 ? 1.0 : 1.5;
  var compactNeeded = 0.6;
  var ultraCompactNeeded = 0.3;

  if (remainingInches >= standardNeeded) return 'standard';
  if (remainingInches >= compactNeeded) return 'compact';
  return 'ultra-compact';
}

/**
 * Core computation: takes physical diagonal, orientation, and habit count,
 * returns { config, proportionalLayout } matching existing component interfaces.
 */
export function computeAdaptiveLayout(diagonalInches, orientation, habitCount) {
  var isLandscape = orientation === 'landscape';

  // Physical dimensions in the current orientation
  var physicalSize = estimatePhysicalSize();
  var contentWidthInches = isLandscape
    ? Math.max(physicalSize.widthInches, physicalSize.heightInches)
    : Math.min(physicalSize.widthInches, physicalSize.heightInches);
  var contentHeightInches = isLandscape
    ? Math.min(physicalSize.widthInches, physicalSize.heightInches)
    : Math.max(physicalSize.widthInches, physicalSize.heightInches);

  // --- Step 2: Space budget ---
  var headerInches = 0.5;
  var availableInches = contentHeightInches - headerInches;

  // How much the weekView needs per habit row (button + spacing)
  // In landscape/grid mode, rows are more compact
  var rowHeight;
  if (isLandscape) {
    rowHeight = diagonalInches < 7 ? 0.25 : 0.35;
  } else {
    rowHeight = diagonalInches < 7 ? 0.35 : 0.5;
  }
  var weekViewOverhead = isLandscape ? 0.5 : 0.8; // nav bar, week label, padding
  var weekViewNeeded = Math.max(1.5, habitCount * rowHeight + weekViewOverhead);

  var remaining = availableInches - weekViewNeeded;

  // --- Step 3: Section visibility ---
  // yearDots always visible, tier depends on remaining space
  var yearDotsTier = determineYearDotsTier(remaining, diagonalInches);

  // Phone portrait: cap yearDots at 'compact' so the week grid (the main
  // interactive surface) gets more of the vertical budget. At 'standard'
  // on a phone, yearDots was claiming ~30% of the height for what's
  // essentially a navigation overview.
  if (!isLandscape && diagonalInches < 7 && yearDotsTier === 'standard') {
    yearDotsTier = 'compact';
  }
  // Landscape: even more aggressive — vertical space is scarce.
  if (isLandscape && yearDotsTier === 'standard') {
    yearDotsTier = 'compact';
  }
  if (isLandscape && diagonalInches < 7) {
    yearDotsTier = 'ultra-compact';
  }

  // Deduct yearDots space from remaining
  var yearDotsSpace;
  if (yearDotsTier === 'standard') {
    yearDotsSpace = diagonalInches < 7 ? 1.0 : 1.5;
  } else if (yearDotsTier === 'compact') {
    yearDotsSpace = 0.6;
  } else {
    yearDotsSpace = 0.3;
  }
  var afterYearDots = remaining - yearDotsSpace;

  // weekProgress: visible on tablets, AND on phone portrait (yearDots is
  // capped at 'compact' there so there's room). Still hidden in landscape
  // because 411 CSS px of height doesn't accommodate a third section.
  var showWeekProgress = !isLandscape && afterYearDots >= 1.0;

  // Tablet portrait gets a 'stripped' weekProgress (just ring + likelihood
  // bar) when vertical room is tight — see the weekProgress block below.
  // When stats is stripped it only needs ~180 CSS px, so we don't have to
  // shrink yearDots to make room: keep it at 'standard' (bigger dots, full
  // chrome). Phone portrait is also stripped (diagonal<7) but is too tight
  // overall, so the legacy downgrade still applies there.
  var statsWillBeStripped =
    showWeekProgress &&
    !isLandscape &&
    diagonalInches >= 7 &&
    diagonalInches < 12 &&
    afterYearDots < 3.5;

  // When all 3 sections are visible, compact yearDots to free space for stats
  if (showWeekProgress && yearDotsTier === 'standard' && !statsWillBeStripped) {
    yearDotsTier = 'compact';
    yearDotsSpace = 0.6;
  }

  // --- Step 4: Flex ratios ---
  var yearDotsFlex;
  if (yearDotsTier === 'ultra-compact') {
    yearDotsFlex = 0.5;
  } else if (yearDotsTier === 'compact') {
    yearDotsFlex = 1;
  } else {
    yearDotsFlex = 2;
  }

  var weekViewFlex = Math.max(2, Math.ceil(habitCount / 2));
  // Stripped stats is short (~180 CSS px) — give it less flex so yearDots
  // and weekView get the remainder.
  var statsFlex;
  if (!showWeekProgress) {
    statsFlex = 0;
  } else if (diagonalInches < 7 || statsWillBeStripped) {
    statsFlex = 1;
  } else {
    statsFlex = 2;
  }

  // --- Step 5: Component sizing ---
  var weekViewLayout;
  if (isLandscape && diagonalInches >= 6) {
    weekViewLayout = 'grid';
  } else {
    weekViewLayout = 'stacked';
  }

  var buttonSize = computeButtonSize(contentWidthInches, diagonalInches, habitCount, orientation);
  var textSize = computeTextSize(diagonalInches);
  var dayTextSize = computeDayTextSize(diagonalInches);
  var dotSize = computeDotSize(diagonalInches, yearDotsTier);
  var yearDotsHeaderSize = computeHeaderTextSize(diagonalInches, yearDotsTier);
  var yearDotsPadding = computePadding(diagonalInches, yearDotsTier);
  // Phone landscape's weekView card gets a tighter padding than computePadding's
  // 'p-2' default — 'p-2' eats 16 CSS px of the ~411 px tall viewport which the
  // grid rows desperately need.
  var weekViewPadding = (isLandscape && diagonalInches < 7)
    ? 'px-2 py-0.5'
    : computePadding(diagonalInches, null);
  var nameColWidth = computeNameColWidth(diagonalInches, orientation);
  var starSize = computeStarSize(diagonalInches);
  var plusSize = computePlusSize(diagonalInches);

  // Header variant
  var headerVariant = diagonalInches < 7 ? 'minimal' : 'standard';

  // yearDots gap
  var yearDotsGap;
  if (yearDotsTier === 'ultra-compact') {
    yearDotsGap = 'gap-[0.0625rem]';
  } else if (yearDotsTier === 'compact') {
    yearDotsGap = 'gap-[0.125rem]';
  } else {
    yearDotsGap = diagonalInches >= 9 ? 'gap-1.5' : 'gap-1';
  }

  // Container spacing. Phones in landscape get tighter vertical padding and
  // a smaller component gap because 411 CSS pixels of height doesn't forgive
  // the 24px top/bottom padding that works fine in portrait.
  var containerPadding;
  if (diagonalInches < 7) {
    containerPadding = isLandscape ? 'px-3 py-1' : 'px-3 py-3';
  } else {
    containerPadding = 'px-6 py-6';
  }
  var componentGap;
  if (diagonalInches < 7) {
    componentGap = isLandscape ? 'space-y-1' : 'space-y-2';
  } else {
    componentGap = 'space-y-4';
  }

  // --- Assemble config ---
  var config = {
    name: 'Adaptive',
    description: diagonalInches.toFixed(1) + '" ' + orientation + ', ' + habitCount + ' habits',
    components: {
      header: {
        visible: true,
        variant: headerVariant,
        showLogo: true,
        showMotivationalMessage: diagonalInches >= 9,
        showDate: diagonalInches >= 7,
        showTodayStats: diagonalInches >= 9,
        showLogout: diagonalInches >= 7,
        layout: 'horizontal'
      },
      yearDots: {
        visible: true, // always visible
        variant: yearDotsTier === 'standard'
          ? (diagonalInches >= 10 ? 'expanded' : 'standard')
          : yearDotsTier,
        orientation: 'horizontal',
        showMonthLabels: yearDotsTier === 'standard' && diagonalInches >= 8,
        showStats: yearDotsTier === 'standard' && diagonalInches >= 8,
        dotSize: dotSize,
        gap: yearDotsGap,
        padding: yearDotsPadding,
        headerSize: yearDotsHeaderSize,
        showLegend: yearDotsTier === 'standard' && diagonalInches >= 8,
        showIcon: yearDotsTier === 'standard' && diagonalInches >= 7,
        isCompact: yearDotsTier !== 'standard',
        showEnhancedTooltips: diagonalInches >= 10 && yearDotsTier === 'standard'
      },
      weekView: {
        visible: true,
        variant: yearDotsTier === 'standard'
          ? (diagonalInches >= 10 ? 'expanded' : 'standard')
          : 'compact',
        showWeekLabel: true,
        showStreaks: diagonalInches >= 7,
        showProgress: true,
        buttonSize: buttonSize,
        starSize: starSize,
        plusSize: plusSize,
        textSize: textSize,
        dayTextSize: dayTextSize,
        padding: weekViewPadding,
        layout: weekViewLayout,
        rowGap: computeRowGap(diagonalInches, orientation, habitCount),
        nameColWidth: weekViewLayout === 'grid' ? nameColWidth : undefined,
        showEnhancedDetails: diagonalInches >= 10
      },
      weekProgress: (function () {
        // 'stripped' drops the title + breakdown grid on phone portrait
        // where vertical room is tight; shows just the progress ring and the
        // success-likelihood bar as a supporting card under the week grid.
        //
        // Tablet portrait (7 ≤ d < 12) can also be tight: yearDots + weekView
        // can leave the stats section with ~400 CSS px while 'standard' needs
        // ~550. `statsWillBeStripped` (computed above) gates this — same
        // condition we use to keep yearDots at 'standard'.
        var variant;
        if (diagonalInches < 7) {
          variant = 'stripped';
        } else if (statsWillBeStripped) {
          variant = 'stripped';
        } else if (diagonalInches >= 12) {
          variant = 'expanded';
        } else {
          variant = 'standard';
        }
        var progressSize;
        var textSize;
        var padding;
        if (variant === 'stripped') {
          // Phone-stripped stays small; tablet-stripped wants a larger ring
          // and slightly more padding so it reads at tablet scale.
          if (diagonalInches < 7) {
            progressSize = 'w-20 h-20';
            textSize = 'text-xl';
            padding = 'p-4';
          } else {
            progressSize = 'w-28 h-28';
            textSize = 'text-2xl';
            padding = 'p-5';
          }
        } else if (diagonalInches >= 10) {
          progressSize = 'w-44 h-44';
          textSize = 'text-5xl';
          padding = 'p-8';
        } else {
          progressSize = 'w-36 h-36';
          textSize = 'text-4xl';
          padding = 'p-6';
        }
        return {
          visible: showWeekProgress,
          variant: variant,
          showGoalCompletion: true,
          showTimeAnalysis: showWeekProgress,
          showHabitStatus: showWeekProgress,
          showDetailedBreakdown: diagonalInches >= 12,
          progressSize: progressSize,
          textSize: textSize,
          padding: padding,
          cardColumns: diagonalInches >= 12 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'
        };
      })()
    },
    spacing: {
      container: containerPadding + ' h-full flex flex-col',
      componentGap: componentGap
    }
  };

  // Total flex for percentage calculations
  var totalFlex = yearDotsFlex + weekViewFlex + statsFlex;

  var proportionalLayout = {
    yearDotsFlex: yearDotsFlex,
    weekViewFlex: weekViewFlex,
    statsFlex: statsFlex,
    showStats: showWeekProgress,
    // Backward-compatible percentage strings
    yearDotsPercent: (yearDotsFlex / totalFlex * 100).toFixed(1) + '%',
    weekViewPercent: (weekViewFlex / totalFlex * 100).toFixed(1) + '%',
    statsPercent: (statsFlex / totalFlex * 100).toFixed(1) + '%'
  };

  return {
    config: config,
    proportionalLayout: proportionalLayout
  };
}

export default {
  estimatePhysicalSize,
  computeAdaptiveLayout
};
